import './pivotSettings.less';
import { observer } from 'mobx-react';
import React from 'react';
import { FormEntryNew, HelpIcon } from 'controls/react/form/formEntryNew';
import { AntSwitch } from 'controls/react/ant/antSwitch';
import { AntSelect } from 'controls/react/ant/antSelect';
import { AntCheckbox } from 'controls/react/ant/antCheckbox';
import { MenuOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import b_ from 'b_';
import { AntButton } from 'controls/react/ant/antButton';
import { i18n } from 'controls/designer/features/widgets/datasources/shared/translations';
import {
	PivotConfigStore,
	PivotModel,
} from 'controls/designer/features/widgets/datasources/shared/utils/pivot-manager/types';

const b = b_.with('pivot-settings');

export const PivotSettings = observer(
	<TModel extends PivotModel>({ store }: { store: PivotConfigStore<TModel> }) => {
		return (
			<>
				<FormEntryNew model={store.model} modelField={'pivotMatrix'} className={b('switch-field')}>
					<AntSwitch size={'small'}></AntSwitch>
					{i18n('Pivot data')}
					<HelpIcon tooltip={i18n('Pivot data lets you turn the datasource table into a summary (pivot) grid.')} />
				</FormEntryNew>
				{store.model.pivotMatrix && (
					<>
						<FormEntryNew
							label={i18n('Row label')}
							model={store.model}
							modelField={'pivotRows'}
							disabled={!store.model.dataSourceId}
							headerAdditionalContent={<CategoryColumnHeaderAdditional store={store} />}
						>
							<AntSelect
								mode={'multiple'}
								nameField={'name'}
								options={store.legendOptions}
								maxTagCount={'responsive'}
								reorderable={true}
								optionRender={store.optionRenderer}
							/>
						</FormEntryNew>
						<FormEntryNew
							label={i18n('Column')}
							model={store.model}
							modelField={'pivotColumns'}
							disabled={!store.model.dataSourceId}
							headerAdditionalContent={<SelectionHeaderAdditional store={store} />}
						>
							<AntSelect
								mode={'multiple'}
								nameField={'name'}
								options={store.columnsOptions}
								reorderable={true}
								optionRender={store.optionRenderer}
							/>
						</FormEntryNew>
						<FormEntryNew
							label={i18n('Value')}
							model={store.model}
							modelField={'pivotValues'}
							disabled={!store.model.dataSourceId || !store.model.pivotRows?.length}
							headerAdditionalContent={<RowHeaderAdditional store={store} />}
						>
							<AntSelect
								mode={'multiple'}
								nameField={'name'}
								options={store.rowsOptions}
								maxTagCount={'responsive'}
								reorderable={true}
								optionRender={store.optionRenderer}
							/>
						</FormEntryNew>
					</>
				)}
				{!store.model.pivotMatrix && (
					<>
						<FormEntryNew
							label={i18n('Row label')}
							model={store.model}
							modelField={'categoryColumns'}
							disabled={!store.model.dataSourceId}
							headerAdditionalContent={<CategoryColumnHeaderAdditional store={store} />}
						>
							<AntSelect
								mode={'multiple'}
								maxTagCount={'responsive'}
								options={store.legendOptions}
								reorderable={true}
								optionRender={store.optionRenderer}
							/>
						</FormEntryNew>
						<FormEntryNew
							label={i18n('Column')}
							model={store.model}
							modelField={'dataColumns'}
							headerAdditionalContent={<SelectionHeaderAdditional store={store} />}
							disabled={!store.model.dataSourceId}
						>
							<AntSelect
								mode={'multiple'}
								nameField={'name'}
								options={store.columnsOptions}
								maxTagCount={'responsive'}
								reorderable={true}
								optionRender={store.optionRenderer}
							/>
						</FormEntryNew>
						<FormEntryNew
							label={i18n('Value')}
							model={store.model}
							modelField={'dataRows'}
							headerAdditionalContent={<RowHeaderAdditional store={store} />}
							disabled={!store.model.dataSourceId || !store.model.categoryColumns?.length}
						>
							<AntSelect
								mode={'multiple'}
								nameField={'name'}
								options={store.rowsOptions}
								maxTagCount={'responsive'}
								reorderable={true}
								optionRender={store.optionRenderer}
							/>
						</FormEntryNew>
					</>
				)}
				<FormEntryNew model={store.model} modelField={'splitPivotValues'} className={b('switch-field')}>
					<AntCheckbox>{i18n('Split values')}</AntCheckbox>{' '}
					<HelpIcon tooltip={i18n('Split values to separate chart columns')} />
				</FormEntryNew>
				<FormEntryNew model={store.model} modelField={'pivotData'} className={b('switch-field')}>
					<AntCheckbox>{i18n('Invert')}</AntCheckbox>{' '}
					<HelpIcon
						tooltip={i18n(
							'Pivot to Data Rows: Displays values from datasource rows instead of displaying them by columns.',
						)}
					/>
				</FormEntryNew>
			</>
		);
	},
);

const CategoryColumnHeaderAdditional = observer(
	<TModel extends PivotModel>({ store }: { store: PivotConfigStore<TModel> }) => {
		return (
			<>
				<HelpIcon
					tooltip={i18n('Row label (index): Selected column(s) define the categories that appear on your chart.')}
				/>
				<AntButton
					disabled={!store.datasourceRuleConfig}
					size={'small'}
					icon={<MenuOutlined />}
					onClick={store.editCategoryColumn}
				></AntButton>
			</>
		);
	},
);

const SelectionHeaderAdditional = observer(
	<TModel extends PivotModel>({ store }: { store: PivotConfigStore<TModel> }) => {
		return (
			<>
				<HelpIcon
					tooltip={i18n('Column: Unique values in this field become headers and are used to aggregate values.')}
				/>
				<AntButton
					disabled={!store.datasourceRuleConfig}
					size={'small'}
					icon={<MenuOutlined />}
					onClick={store.editColumns}
				></AntButton>
			</>
		);
	},
);

const RowHeaderAdditional = observer(
	<TModel extends PivotModel>({ store }: { store: PivotConfigStore<TModel> }) => {
		return (
			<>
				<HelpIcon tooltip={i18n('Values: The numeric field(s) to summarize.')} />
				<AntButton
					disabled={
						!store.datasourceRuleConfig ||
						(store.model.pivotMatrix && !store.model.pivotRows?.length) ||
						(!store.model.pivotMatrix && !store.model.categoryColumns?.length)
					}
					size={'small'}
					icon={<MenuOutlined />}
					onClick={store.editRows}
				></AntButton>
			</>
		);
	},
);
